import { useEffect, useRef } from 'react';
import { useKlaviyoConfigurationQuery } from '../queries/useKlaviyoConfigurationQuery';
import { useKlaviyoCreateSyncMutation } from '../queries/useKlaviyoCreateSyncMutation';
import { useKlaviyoSyncStatusQuery } from '../queries/useKlaviyoSyncStatusQuery';
import { useRefreshKlaviyoListsMutation } from '../queries/useRefreshKlaviyoListsMutation';
import { useSetKlaviyoListsMutation } from '../queries/useSetKlaviyoListsMutation';
export const useKlaviyo = () => {
    const { klaviyoConfigurationQuery, invalidateKlaviyoConfigurationQuery } = useKlaviyoConfigurationQuery();
    const { klaviyoSyncStatusQuery, invalidateKlaviyoSyncStatusQuery } = useKlaviyoSyncStatusQuery();
    const { createKlaviyoSyncMutation } = useKlaviyoCreateSyncMutation();
    const { refreshKlaviyoListsMutation } = useRefreshKlaviyoListsMutation();
    const { setKlaviyoListsMutation } = useSetKlaviyoListsMutation();
    const pollRef = useRef(null);
    const refreshLists = async () => {
        try {
            await refreshKlaviyoListsMutation.mutateAsync([]);
            invalidateKlaviyoConfigurationQuery();
        }
        catch (error) {
            console.error('Failed to refresh Klaviyo lists:', error);
            throw error;
        }
    };
    const setLists = async (requestBody) => {
        try {
            await setKlaviyoListsMutation.mutateAsync([requestBody]);
            invalidateKlaviyoConfigurationQuery();
        }
        catch (error) {
            console.error('Failed to set Klaviyo lists:', error);
            throw error;
        }
    };
    const createSyncJob = async (requestBody) => {
        try {
            await createKlaviyoSyncMutation.mutateAsync([requestBody]);
        }
        catch (error) {
            console.error('Failed to create Klaviyo sync job:', error);
            throw error;
        }
        invalidateKlaviyoSyncStatusQuery();
    };
    useEffect(() => {
        if (klaviyoSyncStatusQuery.data?.activeSync) {
            pollRef.current = setInterval(() => {
                invalidateKlaviyoSyncStatusQuery();
            }, 1000);
        }
        else if (pollRef.current) {
            clearInterval(pollRef.current);
        }
    }, [klaviyoSyncStatusQuery.data?.activeSync]);
    useEffect(() => {
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
            }
        };
    }, []);
    const progress = [
        klaviyoSyncStatusQuery.data?.members
            ? { key: 'MEMBERS', ...klaviyoSyncStatusQuery.data.members }
            : null,
        klaviyoSyncStatusQuery.data?.newsletter
            ? { key: 'NEWSLETTER', ...klaviyoSyncStatusQuery.data.newsletter }
            : null,
        klaviyoSyncStatusQuery.data?.offers
            ? { key: 'PRODUCTS', ...klaviyoSyncStatusQuery.data.offers }
            : null,
        klaviyoSyncStatusQuery.data?.promotions
            ? { key: 'PROMOTIONS', ...klaviyoSyncStatusQuery.data.promotions }
            : null,
        klaviyoSyncStatusQuery.data?.collections
            ? { key: 'COLLECTIONS', ...klaviyoSyncStatusQuery.data.collections }
            : null,
    ]
        .filter((item) => item != null && item.total != null)
        .map((item) => ({
        key: item.key,
        progress: item.progress ?? 0,
        total: item.total,
    }));
    return {
        klaviyo: {
            configuration: klaviyoConfigurationQuery.data,
            isLoading: klaviyoConfigurationQuery.isLoading,
        },
        syncStatus: {
            active: klaviyoSyncStatusQuery.data?.activeSync || false,
            recent: klaviyoSyncStatusQuery.data?.recentSync || false,
            hasSynced: klaviyoSyncStatusQuery.data?.hasSync || false,
            progress,
            isLoading: klaviyoSyncStatusQuery.isLoading,
        },
        refreshLists,
        isRefreshing: refreshKlaviyoListsMutation.isLoading,
        setLists,
        isSettingLists: setKlaviyoListsMutation.isLoading,
        createSyncJob,
        isCreatingSyncJob: createKlaviyoSyncMutation.isLoading,
    };
};
